// Import Firebase services
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, setDoc } from 'firebase/firestore';

// Size conversion mappings between different country standards
const sizeConversionMap = {
  // Women's clothing size conversions
  women: {
    // US sizes as the base
    'US': ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'],
    'UK': ['2', '4', '8', '12', '16', '20', '24', '28'],
    'EU': ['32', '34', '38', '42', '46', '50', '54', '58'],
    'AU': ['4', '6', '10', '14', '18', '22', '26', '30'],
    'NZ': ['4', '6', '10', '14', '18', '22', '26', '30'],
    'CA': ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'],
    'JP': ['3', '5', '7', '9', '11', '13', '15', '17'],
    'CN': ['145/70A', '150/75A', '155/80A', '160/85A', '165/90A', '170/95A', '175/100A', '180/105A'],
    'KR': ['33', '44', '55', '66', '77', '88', '99', '110'],
    'RU': ['40', '42', '44', '46', '48', '50', '52', '54'],
    'BR': ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XXG', '3XG'],
    'MX': ['ECH', 'CH', 'M', 'G', 'EG', 'EEG', '3EG', '4EG'],
    'IN': ['30', '32', '34', '36', '38', '40', '42', '44']
  },
  // Men's clothing size conversions
  men: {
    // US sizes as the base
    'US': ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'],
    'UK': ['32', '34', '38', '42', '46', '50', '54', '58'],
    'EU': ['42', '44', '48', '52', '56', '60', '64', '68'],
    'AU': ['32', '34', '38', '42', '46', '50', '54', '58'],
    'NZ': ['32', '34', '38', '42', '46', '50', '54', '58'],
    'CA': ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'],
    'JP': ['S', 'M', 'L', 'LL', '3L', '4L', '5L', '6L'],
    'CN': ['160/80A', '165/84A', '170/88A', '175/92A', '180/96A', '185/100A', '190/104A', '195/108A'],
    'KR': ['85', '90', '95', '100', '105', '110', '115', '120'],
    'RU': ['44', '46', '50', '54', '58', '62', '66', '70'],
    'BR': ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XXG', '3XG'],
    'MX': ['ECH', 'CH', 'M', 'G', 'EG', 'EEG', '3EG', '4EG'],
    'IN': ['36', '38', '40', '42', '44', '46', '48', '50']
  },
  // Kids clothing size conversions
  kids: {
    'US': ['2T', '3T', '4T', '5', '6', '7', '8', '10', '12', '14', '16'],
    'UK': ['2', '3', '4', '5', '6', '7', '8', '10', '12', '14', '16'],
    'EU': ['92', '98', '104', '110', '116', '122', '128', '140', '152', '164', '176'],
    'AU': ['2', '3', '4', '5', '6', '7', '8', '10', '12', '14', '16'],
    'NZ': ['2', '3', '4', '5', '6', '7', '8', '10', '12', '14', '16']
  }
};

/**
 * Convert a size from one country standard to another
 * @param {string} size - The size to convert
 * @param {string} fromCountry - The source country code (e.g., 'US')
 * @param {string} toCountry - The target country code (e.g., 'EU')
 * @param {string} category - The clothing category ('women', 'men', or 'kids')
 * @returns {string} - The converted size or original if no conversion found
 */
export const convertSize = (size, fromCountry, toCountry, category = 'women') => {
  // If countries are the same, no conversion needed
  if (fromCountry === toCountry) {
    return size;
  }
  
  // Get the conversion map for the specified category
  const conversionMap = sizeConversionMap[category];
  if (!conversionMap) {
    return size; // Return original if category not found
  }
  
  // Get the size arrays for the source and target countries
  const fromSizes = conversionMap[fromCountry];
  const toSizes = conversionMap[toCountry];
  
  if (!fromSizes || !toSizes) {
    return size; // Return original if country not found
  }
  
  // Find the index of the size in the source country's array
  const sizeIndex = fromSizes.findIndex(s => s === size);
  
  // If found and within range of target country's sizes, return the equivalent size
  if (sizeIndex !== -1 && sizeIndex < toSizes.length) {
    return toSizes[sizeIndex];
  }
  
  // Return original size if no conversion found
  return size;
};

/**
 * Generate a sizechart for a target country based on a source sizechart
 * @param {Object} sourceSizechart - The source sizechart object
 * @param {string} targetCountry - The target country code (e.g., 'EU')
 * @param {string} category - The clothing category ('women', 'men', or 'kids')
 * @returns {Object} - A new sizechart object with converted sizes
 */
export const generateConvertedSizechart = (sourceSizechart, targetCountry, category = 'women') => {
  // Determine source country (use the first country in the list)
  const sourceCountry = sourceSizechart.countries[0] || 'US';
  
  // Create a new sizechart object
  const convertedSizechart = {
    ...sourceSizechart,
    id: undefined, // Remove ID so a new one will be generated when saved
    name: `${sourceSizechart.name} (${targetCountry})`,
    countries: [targetCountry],
    rows: []
  };
  
  // Convert each size in the rows
  sourceSizechart.rows.forEach(row => {
    const convertedSize = convertSize(row.size, sourceCountry, targetCountry, category);
    convertedSizechart.rows.push({
      size: convertedSize,
      values: [...row.values] // Copy the measurement values as they are
    });
  });
  
  return convertedSizechart;
};

/**
 * Get all sizechart groups for a shop
 * @param {string} shopId - The shop ID
 * @returns {Promise<Array>} - Array of sizechart groups
 */
export const getSizechartGroups = async (shopId) => {
  try {
    if (!shopId) {
      throw new Error('Shop ID is required');
    }

    const groupsCollection = collection(db, `shops/${shopId}/sizechart_groups`);
    const groupsSnapshot = await getDocs(groupsCollection);
    
    const groups = [];
    groupsSnapshot.forEach(doc => {
      groups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return groups;
  } catch (error) {
    console.error('Error getting sizechart groups:', error);
    throw error;
  }
};

/**
 * Get a specific sizechart group
 * @param {string} shopId - The shop ID
 * @param {string} groupId - The group ID
 * @returns {Promise<Object>} - The sizechart group
 */
export const getSizechartGroup = async (shopId, groupId) => {
  try {
    if (!shopId || !groupId) {
      throw new Error('Shop ID and Group ID are required');
    }

    const groupRef = doc(db, `shops/${shopId}/sizechart_groups`, groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (groupDoc.exists()) {
      return {
        id: groupDoc.id,
        ...groupDoc.data()
      };
    } else {
      throw new Error(`Sizechart group with ID ${groupId} not found`);
    }
  } catch (error) {
    console.error(`Error getting sizechart group ${groupId}:`, error);
    throw error;
  }
};

/**
 * Create a new sizechart group
 * @param {string} shopId - The shop ID
 * @param {Object} group - The group to create
 * @returns {Promise<Object>} - The created group
 */
export const createSizechartGroup = async (shopId, group) => {
  try {
    if (!shopId) {
      throw new Error('Shop ID is required');
    }

    // Prepare the group data
    const groupData = {
      name: group.name,
      description: group.description || '',
      shopId: shopId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const groupsCollection = collection(db, `shops/${shopId}/sizechart_groups`);
    const docRef = await addDoc(groupsCollection, groupData);
    
    return {
      id: docRef.id,
      ...groupData
    };
  } catch (error) {
    console.error('Error creating sizechart group:', error);
    throw error;
  }
};

/**
 * Update an existing sizechart group
 * @param {string} shopId - The shop ID
 * @param {string} groupId - The group ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} - The updated group
 */
export const updateSizechartGroup = async (shopId, groupId, updates) => {
  try {
    if (!shopId || !groupId) {
      throw new Error('Shop ID and Group ID are required');
    }

    const groupRef = doc(db, `shops/${shopId}/sizechart_groups`, groupId);
    
    // Add updatedAt timestamp
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(groupRef, updateData);
    
    // Get the updated document
    const updatedDoc = await getDoc(groupRef);
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error(`Error updating sizechart group ${groupId}:`, error);
    throw error;
  }
};

/**
 * Delete a sizechart group
 * @param {string} shopId - The shop ID
 * @param {string} groupId - The group ID
 * @returns {Promise<void>}
 */
export const deleteSizechartGroup = async (shopId, groupId) => {
  try {
    if (!shopId || !groupId) {
      throw new Error('Shop ID and Group ID are required');
    }

    // First, get all sizecharts in this group and delete them
    const sizechartsCollection = collection(db, `shops/${shopId}/sizecharts`);
    const q = query(sizechartsCollection, where('groupId', '==', groupId));
    const sizechartsSnapshot = await getDocs(q);
    
    const deletePromises = [];
    sizechartsSnapshot.forEach(doc => {
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    // Wait for all sizecharts to be deleted
    await Promise.all(deletePromises);
    
    // Then delete the group
    const groupRef = doc(db, `shops/${shopId}/sizechart_groups`, groupId);
    await deleteDoc(groupRef);
  } catch (error) {
    console.error(`Error deleting sizechart group ${groupId}:`, error);
    throw error;
  }
};

/**
 * Get all sizecharts for a shop
 * @param {string} shopId - The shop ID
 * @param {string} [groupId] - Optional group ID to filter by
 * @returns {Promise<Array>} - Array of sizecharts
 */
export const getSizecharts = async (shopId, groupId = null) => {
  try {
    if (!shopId) {
      throw new Error('Shop ID is required');
    }

    const sizechartsCollection = collection(db, `shops/${shopId}/sizecharts`);
    let sizechartsQuery;
    
    if (groupId) {
      sizechartsQuery = query(sizechartsCollection, where('groupId', '==', groupId));
    } else {
      sizechartsQuery = sizechartsCollection;
    }
    
    const sizechartsSnapshot = await getDocs(sizechartsQuery);
    
    const sizecharts = [];
    sizechartsSnapshot.forEach(doc => {
      sizecharts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return sizecharts;
  } catch (error) {
    console.error('Error getting sizecharts:', error);
    throw error;
  }
};

/**
 * Get a specific sizechart
 * @param {string} shopId - The shop ID
 * @param {string} sizechartId - The sizechart ID
 * @returns {Promise<Object>} - The sizechart
 */
export const getSizechart = async (shopId, sizechartId) => {
  try {
    if (!shopId || !sizechartId) {
      throw new Error('Shop ID and Sizechart ID are required');
    }

    const sizechartRef = doc(db, `shops/${shopId}/sizecharts`, sizechartId);
    const sizechartDoc = await getDoc(sizechartRef);
    
    if (sizechartDoc.exists()) {
      return {
        id: sizechartDoc.id,
        ...sizechartDoc.data()
      };
    } else {
      throw new Error(`Sizechart with ID ${sizechartId} not found`);
    }
  } catch (error) {
    console.error(`Error getting sizechart ${sizechartId}:`, error);
    throw error;
  }
};

/**
 * Create a new sizechart
 * @param {string} shopId - The shop ID
 * @param {Object} sizechart - The sizechart to create
 * @returns {Promise<Object>} - The created sizechart
 */
export const createSizechart = async (shopId, sizechart) => {
  try {
    if (!shopId) {
      throw new Error('Shop ID is required');
    }

    if (!sizechart.groupId) {
      throw new Error('Group ID is required for sizechart');
    }

    // Prepare the sizechart data
    const sizechartData = {
      name: sizechart.name,
      description: sizechart.description || '',
      groupId: sizechart.groupId,
      shopId: shopId,
      columns: sizechart.columns || [],
      rows: sizechart.rows || [],
      countries: sizechart.countries || ['US'],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const sizechartsCollection = collection(db, `shops/${shopId}/sizecharts`);
    const docRef = await addDoc(sizechartsCollection, sizechartData);
    
    return {
      id: docRef.id,
      ...sizechartData
    };
  } catch (error) {
    console.error('Error creating sizechart:', error);
    throw error;
  }
};

/**
 * Update an existing sizechart
 * @param {string} shopId - The shop ID
 * @param {string} sizechartId - The sizechart ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} - The updated sizechart
 */
export const updateSizechart = async (shopId, sizechartId, updates) => {
  try {
    if (!shopId || !sizechartId) {
      throw new Error('Shop ID and Sizechart ID are required');
    }

    const sizechartRef = doc(db, `shops/${shopId}/sizecharts`, sizechartId);
    
    // Add updatedAt timestamp
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(sizechartRef, updateData);
    
    // Get the updated document
    const updatedDoc = await getDoc(sizechartRef);
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error(`Error updating sizechart ${sizechartId}:`, error);
    throw error;
  }
};

/**
 * Delete a sizechart
 * @param {string} shopId - The shop ID
 * @param {string} sizechartId - The sizechart ID
 * @returns {Promise<void>}
 */
export const deleteSizechart = async (shopId, sizechartId) => {
  try {
    if (!shopId || !sizechartId) {
      throw new Error('Shop ID and Sizechart ID are required');
    }

    const sizechartRef = doc(db, `shops/${shopId}/sizecharts`, sizechartId);
    await deleteDoc(sizechartRef);
  } catch (error) {
    console.error(`Error deleting sizechart ${sizechartId}:`, error);
    throw error;
  }
};

/**
 * Get sizechart settings for a shop
 * @param {string} shopId - The shop ID
 * @returns {Promise<Object>} - The sizechart settings
 */
export const getSizechartSettings = async (shopId) => {
  try {
    if (!shopId) {
      throw new Error('Shop ID is required');
    }

    const settingsRef = doc(db, `shops/${shopId}/sizechart_settings`, 'default');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return {
        id: settingsDoc.id,
        ...settingsDoc.data()
      };
    } else {
      // Return minimal default settings if none exist
      // IMPORTANT: Don't provide any default countries
      // Let the user explicitly choose which countries they want
      return {
        id: 'default',
        shopId: shopId,
        defaultCountry: null, // No default country - user must explicitly select
        allowedDeviation: 3,
        countries: [], // Empty array - no default countries
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error(`Error getting sizechart settings for shop ${shopId}:`, error);
    throw error;
  }
};

/**
 * Update sizechart settings for a shop
 * @param {string} shopId - The shop ID
 * @param {Object} settings - The settings to update
 * @returns {Promise<Object>} - The updated settings
 */
export const updateSizechartSettings = async (shopId, settings) => {
  try {
    if (!shopId) {
      throw new Error('Shop ID is required');
    }

    const settingsRef = doc(db, `shops/${shopId}/sizechart_settings`, 'default');
    
    // Check if settings exist
    const settingsDoc = await getDoc(settingsRef);
    
    // Add updatedAt timestamp
    const updateData = {
      ...settings,
      shopId: shopId,
      updatedAt: new Date().toISOString()
    };
    
    if (!settingsDoc.exists()) {
      // Create new settings
      updateData.createdAt = new Date().toISOString();
      await setDoc(settingsRef, updateData);
    } else {
      // Update existing settings
      await updateDoc(settingsRef, updateData);
    }
    
    // Get the updated document
    const updatedDoc = await getDoc(settingsRef);
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error(`Error updating sizechart settings for shop ${shopId}:`, error);
    throw error;
  }
};
